import {
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';

import { CarouselModule } from 'primeng/carousel';

import { IrisIconButtonModule } from '@sondermind/iris/icon-button';
import {
  IAppointmentSlot
} from '@sondermind/data-access/public-facing/provider-calendar';
import { IAppointmentCarouselDate, IAppointmentForCarousel } from './appointment-carousel.interface';

const MAX_NUM_DAYS_VISIBLE: number = 3;
const DEFAULT_TIMES_PER_DATE_INITIAL_BOUNDARY: number = 3;

@Component({
  selector: 'appointment-carousel',
  standalone: true,
  imports: [
    CommonModule,
    IrisIconButtonModule,
    CarouselModule,
    DatePipe
  ],
  templateUrl: './appointment-carousel.component.html',
  styleUrls: ['./appointment-carousel.component.scss'],
})
export class AppointmentCarouselComponent {
  @Input() isAppointmentCarouselLoading: boolean = true;
  @Input() pageSize: number = 3;

  // the number of times you show on a given date before the 'more' link
  @Input() initialNumTimesPerDate: number = DEFAULT_TIMES_PER_DATE_INITIAL_BOUNDARY;

  // when the input of the appointment slots is set, convert the incoming slots
  // to ui elements (carousel dates) and initialize the carousel
  @Input() set appointmentSlots(value: IAppointmentSlot[]) {
    if (value && value.length > 0) {
      this.appointmentCarouselDates = this.processAppointmentSlots(value);
      this.initializeCarousel();
    }
  }

  @Output() appointmentSlotSelectionChanged = new EventEmitter<Date | null>();

  appointmentCarouselDates: IAppointmentCarouselDate[] = [];
  numVisible: number = MAX_NUM_DAYS_VISIBLE;
  selectedTime: Date | null = null;
  showArrows = false;

  processAppointmentSlots(slots: IAppointmentSlot[]): IAppointmentCarouselDate[] {
    // create an array of appt carousel dates to publish to the appt carousel
    // dates hold arrays of appt times
    const apptCarouselDates: IAppointmentCarouselDate[] = [];

    // sort the time slots earliest first
    slots.sort((slotA, slotB) => slotA.start.getTime() - slotB.start.getTime())

      // for each appointment slot
      .forEach((slot: IAppointmentSlot) => {
        // create a ui item for the appointment time
        const apptForCarousel: IAppointmentForCarousel = {
          startTime: slot.start
        };

        // look for an existing ui item for the appointment date
        const foundDate: IAppointmentCarouselDate | undefined = apptCarouselDates.find(
          (apptCarouselDate: IAppointmentCarouselDate) => {
            const carouselDate = apptCarouselDate.date;
            const slotDate = slot.start;

            return carouselDate.getDate() === slotDate.getDate() &&
              carouselDate.getMonth() === slotDate.getMonth() &&
              carouselDate.getFullYear() === slotDate.getFullYear();
          }
        );

        // if the appointment date ui item exists, add the ui element for the time to it
        if (foundDate) {
          foundDate.appointments.push(apptForCarousel);
        } else {
          // if one does not exist, create a ui element for the date
          // and add the time just the same
          const appointmentCarouselDate: IAppointmentCarouselDate = {
            date: slot.start,
            appointments: [apptForCarousel],
            showAllAppointments: false
          };

          apptCarouselDates.push(appointmentCarouselDate);
        }
      });

    return apptCarouselDates;
  }

  private initializeCarousel(): void {
    if (this.appointmentCarouselDates?.length < 1) { return }

    this.numVisible = this.appointmentCarouselDates.length > MAX_NUM_DAYS_VISIBLE
      ? MAX_NUM_DAYS_VISIBLE
      : this.appointmentCarouselDates.length;
  }

  clickAppointmentTime(event: MouseEvent, startTime: Date): void {
    // if the start time is the selected time, unselect it
    this.selectedTime = this.selectedTime === startTime
      ? null
      : startTime;

    // emit the new selection
    this.appointmentSlotSelectionChanged.emit(this.selectedTime);
  }

  seeMoreTimes(apptCarouselDate: IAppointmentCarouselDate): void {
    // eslint-disable-next-line no-param-reassign
    apptCarouselDate.showAllAppointments = true;
  }
}
